import { useState } from 'react';
import { Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { fetchStoryJourney, searchStoryJourneys } from '@/lib/adminApi';

const STATUS_STYLE = {
  completed: 'bg-emerald-600 text-white',
  failed: 'bg-red-600 text-white',
  pending: 'bg-amber-500 text-white',
  running: 'bg-sky-600 text-white',
  retry: 'bg-violet-600 text-white',
  partial: 'bg-orange-500 text-white',
};

const PHASE_FILTERS = [
  { id: '', label: 'All' },
  { id: 'text', label: 'Text' },
  { id: 'audio', label: 'Audio' },
  { id: 'align', label: 'Alignment' },
  { id: 'image', label: 'Image' },
  { id: 'video', label: 'Video' },
  { id: 'created', label: 'Created' },
];

function statusBadge(status) {
  const key = String(status || 'pending').toLowerCase();
  return <Badge className={STATUS_STYLE[key] || 'bg-slate-500 text-white'}>{key}</Badge>;
}

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function countLine(counts) {
  if (!counts) return '';
  const parts = [];
  if (counts.completed) parts.push(`${counts.completed} ok`);
  if (counts.failed) parts.push(`${counts.failed} failed`);
  if (counts.pending) parts.push(`${counts.pending} pending`);
  if (counts.running) parts.push(`${counts.running} running`);
  if (counts.retry) parts.push(`${counts.retry} retrying`);
  if (counts.retries) parts.push(`${counts.retries} retry jobs`);
  return parts.join(' · ') || 'no scenes';
}

export function AdminStoryJourneyPanel() {
  const [queryInput, setQueryInput] = useState('');
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [journey, setJourney] = useState(null);
  const [loadingJourney, setLoadingJourney] = useState(false);
  const [view, setView] = useState('timeline');
  const [phase, setPhase] = useState('');

  const search = async (event) => {
    event?.preventDefault();
    setSearching(true);
    try {
      const data = await searchStoryJourneys(queryInput.trim());
      const items = data.items || [];
      setMatches(items);
      if (items.length === 1) {
        await openJourney(items[0]);
      } else if (!items.length) {
        setJourney(null);
        toast.info('No story or narration project matched that search');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not search stories');
    } finally {
      setSearching(false);
    }
  };

  const openJourney = async (item) => {
    setLoadingJourney(true);
    try {
      const data = await fetchStoryJourney(item.kind, item.id);
      setJourney(data);
      setPhase('');
      setView('timeline');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load this journey');
    } finally {
      setLoadingJourney(false);
    }
  };

  const events = (journey?.events || []).filter((event) => {
    if (phase && event.phase !== phase) return false;
    if (!phase && event.source === 'scene' && event.status === 'pending' && !event.reason) return false;
    return true;
  });
  const lanePhases = (journey?.phases || []).filter((item) => item.id !== 'created');

  return (
    <section className="glass-panel min-w-0 rounded-2xl p-4" data-testid="admin-story-journey">
      <div className="mb-3">
        <h2 className="font-heading text-lg font-semibold">Story journey</h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          Follow one story or narration project from creation through text, audio, image, and video.
          Failures, retries, and the reason for each step stay on the timeline.
        </p>
      </div>

      <form className="mb-4 flex flex-wrap items-center gap-2" onSubmit={search}>
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            className="h-9 pl-8"
            placeholder="Title, id, or narration edit URL"
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
            data-testid="admin-journey-search"
          />
        </div>
        <Button type="submit" size="sm" disabled={searching}>
          {searching ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Search className="mr-1 h-4 w-4" />}
          Search
        </Button>
      </form>

      {matches.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-journey-matches">
          {matches.map((item) => (
            <button
              key={`${item.kind}-${item.id}`}
              type="button"
              className="rounded-xl border border-slate-200 px-3 py-2 text-left text-xs hover:border-indigo-400"
              onClick={() => openJourney(item)}
            >
              <div className="font-medium">{item.title}</div>
              <div className="text-slate-400">{item.kind} · {item.status || 'no status'} · {item.id}</div>
            </button>
          ))}
        </div>
      )}

      {loadingJourney && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
        </div>
      )}

      {!loadingJourney && journey && (
        <div data-testid="admin-journey-detail">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold">{journey.subject?.title}</h3>
              <p className="mt-1 font-mono text-xs text-slate-500">
                {journey.subject?.kind} · {journey.subject?.id}
                {journey.subject?.app ? ` · ${journey.subject.app}` : ''}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Created {formatWhen(journey.subject?.created_at)}
                {journey.subject?.status ? ` · project ${journey.subject.status}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" size="sm" variant={view === 'timeline' ? 'default' : 'outline'} onClick={() => setView('timeline')} data-testid="admin-journey-view-timeline">
                Timeline
              </Button>
              <Button type="button" size="sm" variant={view === 'flow' ? 'default' : 'outline'} onClick={() => setView('flow')} data-testid="admin-journey-view-flow">
                Flowchart
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => openJourney(journey.subject)}>
                <RefreshCw className="mr-1 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-journey-phase-summary">
            {(journey.phases || []).map((item) => (
              <span key={item.id} className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600">
                {item.label}: {countLine(item.counts)}
              </span>
            ))}
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            {(journey.summary?.failure_reasons || []).length === 0 ? (
              <span className="text-xs text-slate-500">No failure or retry reasons recorded.</span>
            ) : (
              journey.summary.failure_reasons.map((item) => (
                <span key={`${item.phase}-${item.reason}`} className="rounded-full bg-red-50 px-3 py-1 text-xs text-red-800">
                  {item.phase} · {item.count}× {item.reason}
                </span>
              ))
            )}
          </div>

          {view === 'flow' ? (
            <div data-testid="admin-journey-flow">
              <div className="mb-4 flex flex-wrap items-stretch gap-2">
                {(journey.phases || []).map((item, index) => (
                  <div key={item.id} className="flex items-center gap-2">
                    <div className="min-w-[140px] rounded-2xl border border-slate-200 bg-white/70 p-3">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold">{item.label}</span>
                        {statusBadge(item.status)}
                      </div>
                      <p className="text-xs text-slate-500">{countLine(item.counts)}</p>
                    </div>
                    {index < journey.phases.length - 1 && <span className="text-slate-300">→</span>}
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200/80">
                <table className="w-full min-w-[720px] text-left text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Scene</th>
                      {lanePhases.map((item) => (
                        <th key={item.id} className="px-3 py-2 font-medium">{item.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(journey.lanes || []).map((lane) => (
                      <tr key={lane.scene_id || lane.label} className="border-t border-slate-100 align-top">
                        <td className="px-3 py-2 font-medium">{lane.label}</td>
                        {lanePhases.map((item) => {
                          const step = lane.steps?.[item.id];
                          if (!step) return <td key={item.id} className="px-3 py-2 text-slate-300">—</td>;
                          return (
                            <td key={item.id} className="px-3 py-2">
                              <div className="mb-1">{statusBadge(step.status)}</div>
                              {step.attempts > 0 && (
                                <div className="text-slate-500">{step.attempts} attempt{step.attempts === 1 ? '' : 's'}</div>
                              )}
                              {step.reason && <div className="mt-1 max-w-[220px] whitespace-normal text-slate-600">{step.reason}</div>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div data-testid="admin-journey-timeline">
              <div className="mb-3 flex flex-wrap gap-2">
                {PHASE_FILTERS.map((item) => (
                  <Button
                    key={item.id || 'all'}
                    type="button"
                    size="sm"
                    variant={phase === item.id ? 'default' : 'outline'}
                    className="rounded-full"
                    onClick={() => setPhase(item.id)}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
              <p className="mb-3 text-xs text-slate-500">
                Pending scenes with no error stay on the flowchart. Filter a step to list them here.
              </p>
              <ol className="relative space-y-3 border-l border-slate-200 pl-4">
                {events.length === 0 ? (
                  <li className="text-sm text-slate-500">No steps for this filter.</li>
                ) : (
                  events.map((event) => (
                    <li key={event.id} className="relative">
                      <span className="absolute -left-[21px] top-2 h-2.5 w-2.5 rounded-full bg-indigo-500" />
                      <div className="rounded-xl border border-slate-200/80 bg-white/60 px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {statusBadge(event.status)}
                          <span className="text-sm font-medium">{event.title}</span>
                          <span className="ml-auto text-xs text-slate-400">{formatWhen(event.at)}</span>
                        </div>
                        {(event.detail || event.reason) && (
                          <p className="mt-1 text-xs text-slate-600">{event.detail || event.reason}</p>
                        )}
                        <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">
                          {event.phase} · {event.source}
                          {event.attempts ? ` · ${event.attempts} attempts` : ''}
                        </p>
                      </div>
                    </li>
                  ))
                )}
              </ol>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
